import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { Observable, BehaviorSubject } from 'rxjs';
import { HeaderHelperService } from '../header-helper.service';
import {
  CheckEmailModel,
  CheckEmailRequest,
} from '../../models/patient/check-email.model';
import { ConsultationHistoryModel } from '../../models/consultation/consultation-history.model';
import { SortModel } from '../../models/filters/sort.model';
import { FilterModel } from '../../models/filters/filters.model';
import {
  SubmitMobilePatientCaseConclusionPayload,
} from '../../models/patient/submit-mobile-patient-case-conclusion.model';

@Injectable({
  providedIn: 'root',
})
export class PatientsService {
  constructor(
    private http: HttpClient,
    private headerHelperService: HeaderHelperService
  ) {}

  private patientIdSubject = new BehaviorSubject<string>('');
  patientId$ = this.patientIdSubject.asObservable();

  setPatientId(id: string) {
    this.patientIdSubject.next(id);
  }

  getPatientId(): string {
    return this.patientIdSubject.getValue();
  }

  patientTest(page: number, size: number): Observable<any> {
    let url = `${environment.apiLocations.patient}/Patient/test`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  patientList(
    page: number,
    size: number,
    filters?: FilterModel[],
    sort?: SortModel
  ): Observable<any> {
    let url = `${
      environment.apiLocations.patient
    }/Patient?PageRequest.PageIndex=${page - 1}&PageRequest.PageSize=${size}`;

    if (filters) {
      const resultFilter = filters.filter((x) => !!x.value);
      url += resultFilter.map((f) => `&${f.filterId}=${f.value}`).join('');
    }
    if (sort) {
      url += `&SortBy=${sort.column}&SortDescending=${
        sort.orderBy == 'descending'
      }`;
    }
    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  dermAiCaseList(
    page: number,
    size: number,
    filters?: FilterModel[],
    sort?: SortModel
  ): Observable<any> {
    let url = `${environment.apiLocations.patient}/MobilePatient/GetAllMobilePatientCase?PageRequest.PageIndex=${
      page - 1
    }&PageRequest.PageSize=${size}`;

    if (filters) {
      const resultFilter = filters.filter((x) => !!x.value);
      url += resultFilter.map((f) => `&${f.filterId}=${f.value}`).join('');
    }
    if (sort) {
      url += `&SortBy=${sort.column}&SortDescending=${
        sort.orderBy == 'descending'
      }`;
    }
    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  getMobilePatientCaseById(caseId: string): Observable<any> {
    const url = `${environment.apiLocations.patient}/MobilePatient/GetMobilePatientCaseById/${caseId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  getMobilePatientCaseImage(
    caseId: string,
    imageType: 'one' | 'two'
  ): Observable<Blob> {
    const url = `${environment.apiLocations.patient}/MobilePatient/GetMobilePatientCaseImage/${caseId}/${imageType}`;

    return this.http.get<Blob>(url, {
      headers: this.headerHelperService.getHeaders(),
      responseType: 'blob' as 'json',
    });
  }

  submitMobilePatientCaseConclusion(
    payload: SubmitMobilePatientCaseConclusionPayload
  ): Observable<any> {
    const url = `${environment.apiLocations.patient}/MobilePatient/SubmitMobilePatientCaseConclusion/${payload.caseId}`;

    return this.http.post<any>(url, payload, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  acquireMobilePatientCaseLock(caseId: string): Observable<any> {
    const url = `${environment.apiLocations.patient}/MobilePatient/AcquireMobilePatientCaseLock/${caseId}`;
    return this.http.post<any>(url, {}, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  renewMobilePatientCaseLock(caseId: string, lockToken: string): Observable<any> {
    const url = `${environment.apiLocations.patient}/MobilePatient/RenewMobilePatientCaseLock/${caseId}`;
    return this.http.post<any>(
      url,
      { lockToken },
      { headers: this.headerHelperService.getHeaders() }
    );
  }

  releaseMobilePatientCaseLock(caseId: string, lockToken: string): Observable<any> {
    const url = `${environment.apiLocations.patient}/MobilePatient/ReleaseMobilePatientCaseLock/${caseId}`;
    return this.http.post<any>(
      url,
      { lockToken },
      { headers: this.headerHelperService.getHeaders() }
    );
  }

  getMobilePatientCaseLocks(): Observable<any[]> {
    const url = `${environment.apiLocations.patient}/MobilePatient/GetMobilePatientCaseLocks`;
    return this.http.get<any[]>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  invoiceList(
    page: number,
    size: number,
    filters?: FilterModel[],
    sort?: SortModel
  ): Observable<any> {
    let url = `${
      environment.apiLocations.patient
    }/Patient?PageRequest.PageIndex=${page - 1}&PageRequest.PageSize=${size}`;

    if (sort) {
      url += `&SortBy=${sort.column}&SortDescending=${
        sort.orderBy == 'descending'
      }`;
    }
    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  patientDeteail(id: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/Patient/${id}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  consultationTimeLineByPatientId(patientId: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetConsultationTimeLineList/${patientId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  getConsultationHistoryByPatientId(
    patientId: string
  ): Observable<ConsultationHistoryModel[]> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetConsultationHistory/${patientId}`;

    return this.http.get<ConsultationHistoryModel[]>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  saveAndTakeImage(caseData: any): Observable<any> {
    return this.http.post<any>(
      `${environment.apiLocations.patient}/PatientRecordCase`,
      caseData,
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );
  }

  patientRecordCaseDetail(patientRecordCaseId: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetPatientRecordCaseDetail/${patientRecordCaseId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  patientRecordCaseImages(patientRecordCaseId: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetPatientRecordCaseImages/${patientRecordCaseId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  consultationTimeLineByCaseId(patientRecordCaseId: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetConsultationTimeLineByCaseId/${patientRecordCaseId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  updateSelectedPatientRecordCaseImage(
    patientRecordCaseId: string
  ): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/UpdateSelectedPatientRecordCaseImage/${patientRecordCaseId}`;

    return this.http.put<any>(
      url,
      {},
      { headers: this.headerHelperService.getHeaders() }
    );
  }

  updateSelectedPatientRecordCaseImageForReport(
    patientRecordCaseId: string,
    form: any
  ): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/ChooseConsultationReportImage/${patientRecordCaseId}`;

    return this.http.post<any>(url, form, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  sendAI(patientRecordCaseId: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/SendAI/${patientRecordCaseId}`;

    return this.http.put<any>(
      url,
      {},
      { headers: this.headerHelperService.getHeaders() }
    );
  }

  consultationTimeLineListByPatientRecordCaseId(
    patientRecordCaseId: string
  ): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetConsultationTimeLineListByPatientRecordCaseId/${patientRecordCaseId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  closePatientRecordCase(
    patientRecordCaseId: string,
    form: any
  ): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/ClosePatientRecordCase/${patientRecordCaseId}`;

    return this.http.put<any>(url, form, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  photoAgain(form: any): Observable<any> {
    return this.http.post<any>(
      `${environment.apiLocations.patient}/PatientRecordCase/TakePhotoNotificationSend`,
      form,
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );
  }

  patientInformationByPID(id: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/Patient/GetPatientInformation/${id}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  patientInformationByEmail(email: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecord/CheckPatientCaseExists/${email}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  dashboardCountInformations(): Observable<any> {
    let url = `${environment.apiLocations.patient}/Dashboard/GetDashboardStatistics`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  bodyImage(id: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetLocationImage/${id}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  appoinmentCaseList(
    page: number,
    size: number,
    filters?: FilterModel[],
    sort?: SortModel
  ): Observable<any> {
    let url = `${
      environment.apiLocations.patient
    }/AppointmentCase?PageRequest.PageIndex=${
      page - 1
    }&PageRequest.PageSize=${size}`;

    if (filters) {
      const resultFilter = filters.filter((x) => !!x.value);
      url += resultFilter.map((f) => `&${f.filterId}=${f.value}`).join('');
    }
    if (sort) {
      url += `&SortBy=${sort.column}&SortDescending=${
        sort.orderBy == 'descending'
      }`;
    }

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  appoinmentCaseDetail(id: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/AppointmentCase/${id}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  patientCaseCardInformation(id: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/Patient/GetPatientCaseCardInformation/${id}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  patientRecordCaseImagesHistory(patientRecordCaseId: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCaseImage/GetReleatedImages/${patientRecordCaseId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  patientReportCaseImages(patientRecordCaseId: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/ReportedCaseImage/GetReportedCaseImages/${patientRecordCaseId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  createPatientRecordWithQrCode(patientRecordCaseId: string): Observable<Blob> {
    let url = `${environment.apiLocations.patient}/QrCode/CreatePatientRecordWithQrCode/${patientRecordCaseId}`;

    return this.http.get<Blob>(url, {
      headers: this.headerHelperService.getHeaders(),
      responseType: 'blob' as 'json',
    });
  }

  getPatientRecordWithQrCode(patientRecordCaseId: string): Observable<Blob> {
    let url = `${environment.apiLocations.patient}/QrCode/GetPatientRecordWithQrCode/${patientRecordCaseId}`;

    return this.http.get<Blob>(url, {
      headers: this.headerHelperService.getHeaders(),
      responseType: 'blob' as 'json',
    });
  }

  getPatientByNameAndBirthDate(params: any): Observable<any> {
    const { name, lastName, birthDate } = params;
    const body = {
      name,
      lastName,
      birthDate,
    };
    return this.http.post<any>(
      `${environment.apiLocations.patient}/Patient/GetPatientByNameAndBirthDate`,
      body,
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );
  }

  dashboardStatistics(type: number): Observable<any> {
    let url = `${environment.apiLocations.patient}/Dashboard/GetDashboardStatistics?RangeType=${type}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  deletePatient(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiLocations.patient}/Patient/${id}`,
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );
  }

  deleteReferralPatient(patientRecordId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiLocations.patient}/PatientRecord/DeleteReferralRequiredPatient/${patientRecordId}`,
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );
  }

  deleteAppointmentCase(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiLocations.patient}/AppointmentCase/${id}`,
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );
  }
}
